import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { reportBuilderService } from '../services/reportBuilderService';
import { showToast } from '@t-one-internal/design-system';
import type {
  DataModel,
  DataModelColumn,
  DataType,
  AggregationType,
  MeasureSpec,
  FilterRow,
  HavingFilterRow,
  CalculatedFieldSpec,
  WindowFunctionSpec,
  ConditionalFormatRule,
  PeriodComparisonSpec,
  SortRow,
  QuerySpec,
  QueryResult,
  ScopingType,
  ReportChartType,
  ResultViewMode,
  ChartFieldMapping,
  PivotConfig,
  VisualizationConfig,
  SavedReport,
  SavedReportConfig,
  TimeGroupingSpec,
} from '../types/reportBuilder';

/** Returns the aggregation functions allowed for a given data type (mirrors backend AllowedAggregationsForType). */
export function getAllowedAggregations(column: DataModelColumn): AggregationType[] {
  if (column.is_measure && column.aggregation_types?.length) {
    return column.aggregation_types;
  }
  // Ad-hoc aggregation: derive from data type
  return getAllowedAggregationsForType(column.data_type);
}

function getAllowedAggregationsForType(dt: DataType): AggregationType[] {
  switch (dt) {
    case 'integer':
    case 'decimal':
      return ['count', 'count_distinct', 'sum', 'avg', 'min', 'max'];
    case 'date':
    case 'timestamp':
      return ['count', 'count_distinct', 'min', 'max'];
    default: // string, boolean, uuid
      return ['count', 'count_distinct'];
  }
}

/** Returns the default aggregation for a column based on its type. */
export function getDefaultAggregation(column: DataModelColumn): AggregationType {
  if (column.is_measure && column.aggregation_types?.length) {
    return column.aggregation_types[0];
  }
  switch (column.data_type) {
    case 'integer':
    case 'decimal':
      return 'sum';
    default:
      return 'count';
  }
}

interface ModelSummary {
  key: string;
  name: string;
  description: string;
  module: string;
  joinable_models: string[];
}

/** A column annotated with its source model key for display grouping. */
export interface AnnotatedColumn extends DataModelColumn {
  sourceModelKey: string;
  sourceModelName: string;
  /** Key used in query references: bare key for primary model, `model.column` for joined models. */
  refKey: string;
}

interface ReportBuilderState {
  // Model catalog
  models: ModelSummary[];
  modelsLoading: boolean;
  selectedModelKeys: string[];
  selectedModel: DataModel | null;
  modelLoading: boolean;

  // Joined models (keyed by model key)
  joinedModels: Record<string, DataModel>;

  // Query building
  selectedDimensions: string[];
  selectedMeasures: MeasureSpec[];
  timeGroupings: TimeGroupingSpec[];
  calculatedFields: CalculatedFieldSpec[];
  windowFunctions: WindowFunctionSpec[];
  periodComparison: PeriodComparisonSpec | null;
  filters: FilterRow[];
  havingFilters: HavingFilterRow[];
  sorts: SortRow[];
  limit: number;
  offset: number;
  scopingType: ScopingType | null;

  // Results
  results: QueryResult | null;
  queryLoading: boolean;
  queryError: string | null;
  previewSQL: string | null;
  debugMode: boolean;

  // Visualization
  conditionalFormats: ConditionalFormatRule[];
  viewMode: ResultViewMode;
  pivotConfig: PivotConfig | null;
  chartType: ReportChartType;
  chartFieldMapping: ChartFieldMapping | null;
  showLegend: boolean;
  showDataLabels: boolean;
  showGridLines: boolean;
  colorScheme: string;

  // Saved reports (localStorage legacy)
  savedReports: SavedReport[];
  saveDialogOpen: boolean;

  // Saved reports (API-backed)
  savedReportConfigs: SavedReportConfig[];
  savedReportsLoading: boolean;
  currentReportId: string | null;

  // Actions
  fetchModels: () => Promise<void>;
  toggleModel: (key: string) => Promise<void>;
  setScopingType: (type: ScopingType) => void;

  // Dimensions
  addDimension: (key: string) => void;
  removeDimension: (key: string) => void;
  setDimensions: (keys: string[]) => void;

  // Measures
  addMeasure: (measure: MeasureSpec) => void;
  removeMeasure: (alias: string) => void;
  updateMeasure: (alias: string, measure: MeasureSpec) => void;

  // Time Groupings
  addTimeGrouping: (tg: TimeGroupingSpec) => void;
  removeTimeGrouping: (column: string) => void;
  clearTimeGroupings: () => void;

  // Filters
  addFilter: (filter: Omit<FilterRow, 'id'>) => void;
  removeFilter: (id: string) => void;
  updateFilter: (id: string, filter: Partial<FilterRow>) => void;

  // Having Filters
  addHavingFilter: (filter: Omit<HavingFilterRow, 'id'>) => void;
  removeHavingFilter: (id: string) => void;
  updateHavingFilter: (id: string, filter: Partial<HavingFilterRow>) => void;

  // Calculated Fields
  addCalculatedField: (field: CalculatedFieldSpec) => void;
  removeCalculatedField: (key: string) => void;
  updateCalculatedField: (key: string, field: CalculatedFieldSpec) => void;

  // Window Functions
  addWindowFunction: (wf: WindowFunctionSpec) => void;
  removeWindowFunction: (key: string) => void;
  updateWindowFunction: (key: string, wf: WindowFunctionSpec) => void;

  // Period Comparison
  setPeriodComparison: (pc: PeriodComparisonSpec | null) => void;

  // Sorts
  addSort: (sort: Omit<SortRow, 'id'>) => void;
  removeSort: (id: string) => void;
  updateSort: (id: string, sort: Partial<SortRow>) => void;

  // Query actions
  buildQuerySpec: () => QuerySpec | null;
  executeQuery: () => Promise<void>;
  fetchPreviewSQL: () => Promise<void>;
  setOffset: (offset: number) => void;
  setLimit: (limit: number) => void;
  resetQuery: () => void;
  toggleDebugMode: () => void;

  // Conditional Formatting
  addConditionalFormat: (rule: ConditionalFormatRule) => void;
  removeConditionalFormat: (id: string) => void;

  // Visualization actions
  setViewMode: (mode: ResultViewMode) => void;
  setPivotConfig: (config: PivotConfig | null) => void;
  autoDetectPivotConfig: () => void;
  setChartType: (type: ReportChartType) => void;
  setChartFieldMapping: (mapping: ChartFieldMapping | null) => void;
  setShowLegend: (show: boolean) => void;
  setShowDataLabels: (show: boolean) => void;
  setShowGridLines: (show: boolean) => void;
  setColorScheme: (scheme: string) => void;
  autoDetectFieldMapping: () => void;
  getVisualizationConfig: () => VisualizationConfig | null;

  // Saved report actions (localStorage legacy)
  loadSavedReports: () => void;
  saveReport: (name: string, description: string) => void;
  loadReport: (id: string) => Promise<void>;
  deleteReport: (id: string) => void;
  setSaveDialogOpen: (open: boolean) => void;

  // Saved report actions (API-backed)
  fetchSavedReportConfigs: () => Promise<void>;
  saveReportToAPI: (name: string, description: string) => Promise<SavedReportConfig | null>;
  updateReportToAPI: (id: string, name: string, description: string) => Promise<SavedReportConfig | null>;
  deleteReportFromAPI: (id: string) => Promise<void>;
  loadReportFromAPI: (id: string) => Promise<void>;
  setCurrentReportId: (id: string | null) => void;

  // Computed helpers (merged across primary + joined models)
  getJoinableModelKeys: () => Set<string>;
  getDimensionColumns: () => AnnotatedColumn[];
  getMeasureColumns: () => AnnotatedColumn[];
  getFilterableColumns: () => AnnotatedColumn[];
  getSortableColumns: () => Array<{ key: string; label: string; sourceModelName: string }>;
  getDateColumns: () => AnnotatedColumn[];
  getAllColumns: () => Record<string, DataModelColumn>;
}

/** Collect ordered columns from a model, annotated with source info. */
function annotateColumns(model: DataModel, isPrimary: boolean): AnnotatedColumn[] {
  return model.column_order
    .map(key => model.columns[key])
    .filter(Boolean)
    .map(col => ({
      ...col,
      sourceModelKey: model.key,
      sourceModelName: model.name,
      refKey: isPrimary ? col.key : `${model.key}.${col.key}`,
    }));
}

/** Remove columns/measures/filters/timeGroupings referencing any column in the given model. */
function cleanupModelReferences(
  model: DataModel,
  state: Pick<ReportBuilderState, 'selectedDimensions' | 'selectedMeasures' | 'filters' | 'timeGroupings'>
) {
  const bareKeys = new Set(Object.keys(model.columns));
  const qualifiedKeys = new Set(Object.keys(model.columns).map(k => `${model.key}.${k}`));
  const matches = (key: string) => bareKeys.has(key) || qualifiedKeys.has(key);
  return {
    selectedDimensions: state.selectedDimensions.filter(d => !matches(d)),
    selectedMeasures: state.selectedMeasures.filter(m => !matches(m.column)),
    timeGroupings: state.timeGroupings.filter(tg => !matches(tg.column)),
    filters: state.filters.filter(f => !matches(f.column)),
  };
}

export const useReportBuilderStore = create<ReportBuilderState>()(
  devtools(
    (set, get) => ({
      // Initial state
      models: [],
      modelsLoading: false,
      selectedModelKeys: [],
      selectedModel: null,
      modelLoading: false,

      joinedModels: {},

      selectedDimensions: [],
      selectedMeasures: [],
      timeGroupings: [],
      calculatedFields: [],
      windowFunctions: [],
      periodComparison: null,
      filters: [],
      havingFilters: [],
      sorts: [],
      limit: 100,
      offset: 0,
      scopingType: null,

      results: null,
      queryLoading: false,
      queryError: null,
      previewSQL: null,
      debugMode: false,

      conditionalFormats: [],
      viewMode: 'table',
      pivotConfig: null,
      chartType: 'column',
      chartFieldMapping: null,
      showLegend: true,
      showDataLabels: false,
      showGridLines: true,
      colorScheme: 'default',

      savedReports: [],
      saveDialogOpen: false,
      savedReportConfigs: [],
      savedReportsLoading: false,
      currentReportId: null,

      // --- Actions ---

      fetchModels: async () => {
        set({ modelsLoading: true });
        try {
          const resp = await reportBuilderService.listModels();
          set({ models: resp.models, modelsLoading: false });
        } catch (e) {
          console.error('Failed to fetch models:', e);
          set({ modelsLoading: false });
        }
      },

      toggleModel: async (key: string) => {
        const { selectedModelKeys, models, joinedModels, selectedModel } = get();
        const isSelected = selectedModelKeys.includes(key);

        if (isSelected) {
          // --- Deselecting ---
          const isPrimary = selectedModelKeys[0] === key;

          if (selectedModelKeys.length === 1) {
            set({
              selectedModelKeys: [], selectedModel: null, joinedModels: {},
              selectedDimensions: [], selectedMeasures: [], timeGroupings: [], calculatedFields: [], windowFunctions: [], periodComparison: null, filters: [], havingFilters: [], sorts: [],
              offset: 0, results: null, queryError: null, previewSQL: null,
            });
            return;
          }

          if (!isPrimary) {
            const model = joinedModels[key];
            const cleaned = model
              ? cleanupModelReferences(model, get())
              : { selectedDimensions: get().selectedDimensions, selectedMeasures: get().selectedMeasures, timeGroupings: get().timeGroupings, filters: get().filters };
            const newJoined = { ...joinedModels };
            delete newJoined[key];
            set({ selectedModelKeys: selectedModelKeys.filter(k => k !== key), joinedModels: newJoined, ...cleaned });
            return;
          }

          // Removing the primary: promote next model
          const remaining = selectedModelKeys.slice(1);
          const newPrimaryKey = remaining[0];
          const newPrimaryMeta = models.find(m => m.key === newPrimaryKey);
          const newPrimaryJoinable = new Set(newPrimaryMeta?.joinable_models ?? []);

          const validKeys = [newPrimaryKey];
          const invalidKeys: string[] = [];
          for (const k of remaining.slice(1)) {
            if (newPrimaryJoinable.has(k)) validKeys.push(k);
            else invalidKeys.push(k);
          }

          let cleaned = selectedModel
            ? cleanupModelReferences(selectedModel, get())
            : { selectedDimensions: get().selectedDimensions, selectedMeasures: get().selectedMeasures, timeGroupings: get().timeGroupings, filters: get().filters };
          for (const ik of invalidKeys) {
            const m = joinedModels[ik];
            if (m) cleaned = cleanupModelReferences(m, cleaned);
          }

          // Re-key promoted model's references from qualified ("model.col") to bare ("col")
          const prefix = `${newPrimaryKey}.`;
          cleaned.selectedDimensions = cleaned.selectedDimensions.map(d =>
            d.startsWith(prefix) ? d.slice(prefix.length) : d
          );
          cleaned.selectedMeasures = cleaned.selectedMeasures.map(m =>
            m.column.startsWith(prefix)
              ? { ...m, column: m.column.slice(prefix.length), alias: `${m.aggregation}_${m.column.slice(prefix.length)}` }
              : m
          );
          cleaned.timeGroupings = cleaned.timeGroupings.map(tg =>
            tg.column.startsWith(prefix) ? { ...tg, column: tg.column.slice(prefix.length) } : tg
          );
          cleaned.filters = cleaned.filters.map(f =>
            f.column.startsWith(prefix) ? { ...f, column: f.column.slice(prefix.length) } : f
          );

          const promotedModel = joinedModels[newPrimaryKey] ?? null;
          const newJoined: Record<string, DataModel> = {};
          for (const k of validKeys.slice(1)) {
            if (joinedModels[k]) newJoined[k] = joinedModels[k];
          }

          set({
            selectedModelKeys: validKeys, selectedModel: promotedModel,
            joinedModels: newJoined, modelLoading: !promotedModel, ...cleaned,
          });

          if (!promotedModel) {
            try {
              const model = await reportBuilderService.getModel(newPrimaryKey);
              set({ selectedModel: model, modelLoading: false });
            } catch (e) {
              console.error('Failed to fetch promoted model:', e);
              set({ modelLoading: false });
            }
          }
          return;
        }

        // --- Selecting ---
        if (selectedModelKeys.length === 0) {
          set({
            selectedModelKeys: [key], modelLoading: true,
            selectedDimensions: [], selectedMeasures: [], timeGroupings: [], calculatedFields: [], windowFunctions: [], periodComparison: null, filters: [], havingFilters: [], sorts: [],
            offset: 0, results: null, queryError: null, previewSQL: null, joinedModels: {},
          });
          try {
            const model = await reportBuilderService.getModel(key);
            set({ selectedModel: model, modelLoading: false });
          } catch (e) {
            console.error('Failed to fetch model:', e);
            set({ modelLoading: false });
          }
        } else {
          set({ modelLoading: true });
          try {
            const model = await reportBuilderService.getModel(key);
            set({
              selectedModelKeys: [...selectedModelKeys, key],
              joinedModels: { ...joinedModels, [key]: model },
              modelLoading: false,
            });
          } catch (e) {
            console.error('Failed to fetch join model:', e);
            set({ modelLoading: false });
          }
        }
      },

      setScopingType: (type: ScopingType) => set({ scopingType: type }),

      // Dimensions
      addDimension: (key) => {
        const { selectedDimensions } = get();
        if (!selectedDimensions.includes(key)) set({ selectedDimensions: [...selectedDimensions, key] });
      },
      removeDimension: (key) => set({ selectedDimensions: get().selectedDimensions.filter(d => d !== key) }),
      setDimensions: (keys) => set({ selectedDimensions: keys }),

      // Measures
      addMeasure: (m) => {
        const { selectedMeasures } = get();
        if (selectedMeasures.some(x => x.alias === m.alias || x.column === m.column)) return;
        set({ selectedMeasures: [...selectedMeasures, m] });
      },
      removeMeasure: (alias) => set({ selectedMeasures: get().selectedMeasures.filter(m => m.alias !== alias) }),
      updateMeasure: (alias, m) => set({ selectedMeasures: get().selectedMeasures.map(x => x.alias === alias ? m : x) }),

      // Time Groupings
      addTimeGrouping: (tg) => {
        const { timeGroupings } = get();
        if (!timeGroupings.some(g => g.column === tg.column && g.granularity === tg.granularity)) {
          set({ timeGroupings: [...timeGroupings, tg] });
        }
      },
      removeTimeGrouping: (column) => set({ timeGroupings: get().timeGroupings.filter(tg => tg.column !== column) }),
      clearTimeGroupings: () => set({ timeGroupings: [] }),

      // Filters
      addFilter: (f) => set({ filters: [...get().filters, { ...f, id: crypto.randomUUID() }] }),
      removeFilter: (id) => set({ filters: get().filters.filter(f => f.id !== id) }),
      updateFilter: (id, p) => set({ filters: get().filters.map(f => f.id === id ? { ...f, ...p } : f) }),

      // Having Filters
      addHavingFilter: (f) => set({ havingFilters: [...get().havingFilters, { ...f, id: crypto.randomUUID() }] }),
      removeHavingFilter: (id) => set({ havingFilters: get().havingFilters.filter(f => f.id !== id) }),
      updateHavingFilter: (id, p) => set({ havingFilters: get().havingFilters.map(f => f.id === id ? { ...f, ...p } : f) }),

      // Calculated Fields
      addCalculatedField: (field) => {
        const { calculatedFields } = get();
        if (!calculatedFields.some(f => f.key === field.key)) {
          set({ calculatedFields: [...calculatedFields, field] });
        }
      },
      removeCalculatedField: (key) => set({ calculatedFields: get().calculatedFields.filter(f => f.key !== key) }),
      updateCalculatedField: (key, field) => set({ calculatedFields: get().calculatedFields.map(f => f.key === key ? field : f) }),

      // Window Functions
      addWindowFunction: (wf) => {
        const { windowFunctions } = get();
        if (!windowFunctions.some(w => w.key === wf.key)) {
          set({ windowFunctions: [...windowFunctions, wf] });
        }
      },
      removeWindowFunction: (key) => set({ windowFunctions: get().windowFunctions.filter(w => w.key !== key) }),
      updateWindowFunction: (key, wf) => set({ windowFunctions: get().windowFunctions.map(w => w.key === key ? wf : w) }),

      // Period Comparison
      setPeriodComparison: (pc) => set({ periodComparison: pc }),

      // Sorts
      addSort: (s) => set({ sorts: [...get().sorts, { ...s, id: crypto.randomUUID() }] }),
      removeSort: (id) => set({ sorts: get().sorts.filter(s => s.id !== id) }),
      updateSort: (id, p) => set({ sorts: get().sorts.map(s => s.id === id ? { ...s, ...p } : s) }),

      // Build QuerySpec from current state
      buildQuerySpec: (): QuerySpec | null => {
        const { selectedModelKeys, selectedDimensions, selectedMeasures, timeGroupings, calculatedFields, windowFunctions, periodComparison, filters, havingFilters, sorts, limit, offset, scopingType } = get();
        if (selectedModelKeys.length === 0) return null;
        if (selectedDimensions.length === 0 && selectedMeasures.length === 0 && timeGroupings.length === 0) return null;

        // Deduplicate dimensions (keep first occurrence)
        const uniqueDims = [...new Set(selectedDimensions)];
        const spec: QuerySpec = { model: selectedModelKeys[0], columns: uniqueDims, limit, offset };

        if (selectedModelKeys.length > 1) {
          spec.join_models = selectedModelKeys.slice(1);
        }
        if (selectedMeasures.length > 0) {
          // Deduplicate measures by alias (keep first occurrence)
          const seenAliases = new Set<string>();
          spec.measures = selectedMeasures
            .filter(({ alias }) => {
              if (seenAliases.has(alias)) return false;
              seenAliases.add(alias);
              return true;
            })
            .map(({ column, aggregation, alias }) => ({ column, aggregation, alias }));
        }
        if (timeGroupings.length > 0) {
          spec.time_groupings = timeGroupings.map(({ column, granularity }) => ({ column, granularity }));
        }
        // ISU-5: Filter out entries where value is empty/null/undefined before sending to API.
        // Operators like is_null / is_not_null intentionally have null values and are kept.
        const NO_VALUE_OPERATORS: string[] = ['is_null', 'is_not_null'];
        const validFilters = filters.filter(({ operator, value }) => {
          if (NO_VALUE_OPERATORS.includes(operator)) return true;
          if (value === null || value === undefined) return false;
          if (typeof value === 'string' && value.trim() === '') return false;
          if (Array.isArray(value) && value.length === 0) return false;
          return true;
        });
        if (validFilters.length > 0) {
          spec.filters = validFilters.map(({ column, operator, value }) => ({ column, operator, value }));
        }
        if (havingFilters.length > 0) {
          spec.having_filters = havingFilters.map(({ measure_alias, operator, value }) => ({ measure_alias, operator, value }));
        }
        if (calculatedFields.length > 0) {
          spec.calculated_fields = calculatedFields;
        }
        if (windowFunctions.length > 0) {
          spec.window_functions = windowFunctions;
        }
        if (periodComparison) {
          spec.period_comparison = periodComparison;
        }
        if (sorts.length > 0) {
          spec.sort = sorts.map(({ column, direction }) => ({ column, direction }));
        }
        if (scopingType) spec.scoping_type = scopingType;
        return spec;
      },

      executeQuery: async () => {
        const spec = get().buildQuerySpec();
        if (!spec) return;
        set({ queryLoading: true, queryError: null });
        try {
          const result = await reportBuilderService.executeQuery(spec);
          set({ results: result, queryLoading: false });
        } catch (e: unknown) {
          const message = e instanceof Error ? e.message
            : (e && typeof e === 'object' && 'response' in e)
              ? ((e as { response?: { data?: { details?: string; error?: string } } }).response?.data?.details
                ?? (e as { response?: { data?: { error?: string } } }).response?.data?.error
                ?? 'Query failed')
              : 'Query failed';
          set({ queryError: message, queryLoading: false });
        }
      },

      fetchPreviewSQL: async () => {
        const spec = get().buildQuerySpec();
        if (!spec) return;
        try {
          const resp = await reportBuilderService.previewSQL(spec);
          set({ previewSQL: resp.sql });
        } catch (e) {
          console.error('Failed to fetch preview SQL:', e);
        }
      },

      setOffset: (offset) => set({ offset }),
      setLimit: (limit) => set({ limit }),

      resetQuery: () => set({
        selectedModelKeys: [], selectedModel: null, joinedModels: {},
        selectedDimensions: [], selectedMeasures: [], timeGroupings: [], calculatedFields: [], windowFunctions: [], periodComparison: null, filters: [], havingFilters: [], sorts: [],
        offset: 0, results: null, queryError: null, previewSQL: null,
        viewMode: 'table', chartType: 'column', chartFieldMapping: null,
        showLegend: true, showDataLabels: false, showGridLines: true, colorScheme: 'default',
        scopingType: null, currentReportId: null,
      }),

      toggleDebugMode: () => set(s => ({ debugMode: !s.debugMode })),

      // Conditional Formatting
      addConditionalFormat: (rule) => set({ conditionalFormats: [...get().conditionalFormats, rule] }),
      removeConditionalFormat: (id) => set({ conditionalFormats: get().conditionalFormats.filter(r => r.id !== id) }),

      // --- Visualization actions ---

      setViewMode: (mode) => set({ viewMode: mode }),
      setPivotConfig: (config) => set({ pivotConfig: config }),
      autoDetectPivotConfig: () => {
        const { results } = get();
        if (!results) return;
        const dims = results.columns.filter(c => c.is_dimension || c.is_time_grouping);
        const measures = results.columns.filter(c => c.is_measure);
        if (dims.length >= 2 && measures.length >= 1) {
          set({
            pivotConfig: {
              rowField: dims[0].key,
              columnField: dims[1].key,
              valueField: measures[0].key,
              showRowTotals: true,
              showColumnTotals: true,
            },
          });
        }
      },
      setChartType: (type) => set({ chartType: type }),
      setChartFieldMapping: (mapping) => set({ chartFieldMapping: mapping }),
      setShowLegend: (show) => set({ showLegend: show }),
      setShowDataLabels: (show) => set({ showDataLabels: show }),
      setShowGridLines: (show) => set({ showGridLines: show }),
      setColorScheme: (scheme) => set({ colorScheme: scheme }),

      autoDetectFieldMapping: () => {
        const { results, selectedDimensions, selectedMeasures } = get();
        if (!results) return;
        const dimensionCols = results.columns.filter(c => c.is_dimension);
        const measureCols = results.columns.filter(c => c.is_measure);
        // Prefer time grouping columns as X-axis when available
        const timeGroupCol = dimensionCols.find(c => c.is_time_grouping);
        const nameField = timeGroupCol?.key
          ?? dimensionCols.find(c => selectedDimensions.includes(c.key))?.key
          ?? dimensionCols[0]?.key ?? '';
        const valueField = measureCols.find(c => selectedMeasures.some(m => m.alias === c.key))?.key ?? measureCols[0]?.key ?? '';
        // If 2+ dimensions, use the second as series/color
        const seriesField = dimensionCols.length >= 2
          ? dimensionCols.find(c => c.key !== nameField)?.key
          : undefined;
        if (nameField && valueField) set({ chartFieldMapping: { nameField, valueField, seriesField } });
      },

      getVisualizationConfig: (): VisualizationConfig | null => {
        const { chartType, chartFieldMapping, showLegend, showDataLabels } = get();
        if (!chartFieldMapping) return null;
        return { chartType, fieldMapping: chartFieldMapping, showLegend, showDataLabels };
      },

      // --- Saved report actions ---

      setSaveDialogOpen: (open) => set({ saveDialogOpen: open }),

      loadSavedReports: () => {
        try {
          const raw = localStorage.getItem('report-builder-saved-reports');
          if (raw) set({ savedReports: JSON.parse(raw) });
        } catch (e) {
          console.error('Failed to load saved reports:', e);
        }
      },

      saveReport: (name, description) => {
        const state = get();
        const spec = state.buildQuerySpec();
        if (!spec) return;
        const now = new Date().toISOString();
        const report: SavedReport = {
          id: crypto.randomUUID(), name, description, createdAt: now, updatedAt: now,
          querySpec: spec, selectedModelKeys: state.selectedModelKeys,
          selectedDimensions: state.selectedDimensions, selectedMeasures: state.selectedMeasures,
          timeGroupings: state.timeGroupings, filters: state.filters, sorts: state.sorts, limit: state.limit,
          visualization: state.getVisualizationConfig(), viewMode: state.viewMode,
        };
        const updated = [...state.savedReports, report];
        localStorage.setItem('report-builder-saved-reports', JSON.stringify(updated));
        set({ savedReports: updated, saveDialogOpen: false });
      },

      loadReport: async (id) => {
        const { savedReports } = get();
        const report = savedReports.find(r => r.id === id);
        if (!report) return;

        set({
          selectedModelKeys: [], selectedModel: null, joinedModels: {},
          selectedDimensions: [], selectedMeasures: [], timeGroupings: [], calculatedFields: [], windowFunctions: [], periodComparison: null, filters: [], havingFilters: [], sorts: [],
          offset: 0, results: null, queryError: null, previewSQL: null,
          viewMode: 'table', chartType: 'column', chartFieldMapping: null,
          showLegend: true, showDataLabels: false, queryLoading: true,
          currentReportId: id,
        });

        try {
          for (const key of report.selectedModelKeys) {
            if (get().currentReportId !== id) return; // aborted — user navigated away
            await get().toggleModel(key);
          }
          if (get().currentReportId !== id) return;
          set({
            selectedDimensions: report.selectedDimensions, selectedMeasures: report.selectedMeasures,
            timeGroupings: report.timeGroupings ?? [], filters: report.filters, sorts: report.sorts,
            limit: report.limit, viewMode: report.viewMode,
          });
          if (report.visualization) {
            set({
              chartType: report.visualization.chartType,
              chartFieldMapping: report.visualization.fieldMapping,
              showLegend: report.visualization.showLegend,
              showDataLabels: report.visualization.showDataLabels,
            });
          }
          if (get().currentReportId !== id) return;
          await get().executeQuery();
        } catch (e) {
          console.error('Failed to load report:', e);
          if (get().currentReportId === id) {
            set({ queryLoading: false, queryError: 'Failed to load report' });
          }
        }
      },

      deleteReport: (id) => {
        const { savedReports } = get();
        const updated = savedReports.filter(r => r.id !== id);
        localStorage.setItem('report-builder-saved-reports', JSON.stringify(updated));
        set({ savedReports: updated });
        showToast.success('Report deleted', { description: 'The report has been deleted successfully.' });
      },

      // --- API-backed saved report actions ---

      setCurrentReportId: (id) => set({ currentReportId: id }),

      fetchSavedReportConfigs: async () => {
        set({ savedReportsLoading: true });
        try {
          const configs = await reportBuilderService.listSavedReports();
          set({ savedReportConfigs: configs, savedReportsLoading: false });
        } catch (e) {
          console.error('Failed to fetch saved report configs:', e);
          set({ savedReportsLoading: false });
        }
      },

      saveReportToAPI: async (name, description) => {
        const state = get();
        const spec = state.buildQuerySpec();
        if (!spec) return null;

        const builderState = {
          selectedDimensions: state.selectedDimensions,
          selectedMeasures: state.selectedMeasures,
          timeGroupings: state.timeGroupings,
          calculatedFields: state.calculatedFields,
          windowFunctions: state.windowFunctions,
          periodComparison: state.periodComparison ?? undefined,
          filters: state.filters,
          havingFilters: state.havingFilters,
          sorts: state.sorts,
          limit: state.limit,
          viewMode: state.viewMode,
          scopingType: state.scopingType ?? undefined,
        };

        try {
          const config = await reportBuilderService.createSavedReport({
            name,
            description,
            query_spec: spec,
            visualization_config: state.getVisualizationConfig(),
            model_keys: state.selectedModelKeys,
            builder_state: builderState,
          });
          set({ saveDialogOpen: false });
          showToast.success('Report created', { description: `"${name}" has been saved successfully.` });
          return config;
        } catch (e) {
          console.error('Failed to save report to API:', e);
          showToast.error('Failed to create report', { description: 'Something went wrong. Please try again.' });
          return null;
        }
      },

      updateReportToAPI: async (id, name, description) => {
        const state = get();
        const spec = state.buildQuerySpec();
        if (!spec) return null;

        const builderState = {
          selectedDimensions: state.selectedDimensions,
          selectedMeasures: state.selectedMeasures,
          timeGroupings: state.timeGroupings,
          calculatedFields: state.calculatedFields,
          windowFunctions: state.windowFunctions,
          periodComparison: state.periodComparison ?? undefined,
          filters: state.filters,
          havingFilters: state.havingFilters,
          sorts: state.sorts,
          limit: state.limit,
          viewMode: state.viewMode,
          scopingType: state.scopingType ?? undefined,
        };

        try {
          const config = await reportBuilderService.updateSavedReport(id, {
            name,
            description,
            query_spec: spec,
            visualization_config: state.getVisualizationConfig(),
            model_keys: state.selectedModelKeys,
            builder_state: builderState,
          });
          showToast.success('Report updated', { description: `"${name}" has been updated successfully.` });
          return config;
        } catch (e) {
          console.error('Failed to update report:', e);
          showToast.error('Failed to update report', { description: 'Something went wrong. Please try again.' });
          return null;
        }
      },

      deleteReportFromAPI: async (id) => {
        try {
          await reportBuilderService.deleteSavedReport(id);
          set({ savedReportConfigs: get().savedReportConfigs.filter(c => c.id !== id) });
          showToast.success('Report deleted', { description: 'The report has been deleted successfully.' });
        } catch (e) {
          console.error('Failed to delete report:', e);
          showToast.error('Failed to delete report', { description: 'Something went wrong. Please try again.' });
        }
      },

      loadReportFromAPI: async (id) => {
        set({
          selectedModelKeys: [], selectedModel: null, joinedModels: {},
          selectedDimensions: [], selectedMeasures: [], timeGroupings: [], calculatedFields: [], windowFunctions: [], periodComparison: null, filters: [], havingFilters: [], sorts: [],
          offset: 0, results: null, queryError: null, previewSQL: null,
          viewMode: 'table', chartType: 'column', chartFieldMapping: null,
          showLegend: true, showDataLabels: false, queryLoading: true,
          currentReportId: id,
        });

        try {
          const report = await reportBuilderService.getSavedReport(id);
          if (get().currentReportId !== id) return; // aborted — user navigated away

          // Hydrate models
          for (const key of report.model_keys) {
            if (get().currentReportId !== id) return;
            await get().toggleModel(key);
          }

          if (get().currentReportId !== id) return;

          // Hydrate builder state
          if (report.builder_state) {
            set({
              selectedDimensions: report.builder_state.selectedDimensions,
              selectedMeasures: report.builder_state.selectedMeasures,
              timeGroupings: report.builder_state.timeGroupings ?? [],
              calculatedFields: report.builder_state.calculatedFields ?? [],
              windowFunctions: report.builder_state.windowFunctions ?? [],
              periodComparison: report.builder_state.periodComparison ?? null,
              filters: report.builder_state.filters,
              havingFilters: report.builder_state.havingFilters ?? [],
              sorts: report.builder_state.sorts,
              limit: report.builder_state.limit,
              viewMode: report.builder_state.viewMode,
              scopingType: report.builder_state.scopingType ?? report.query_spec.scoping_type ?? null,
            });
          }

          // Hydrate visualization config
          if (report.visualization_config) {
            set({
              chartType: report.visualization_config.chartType,
              chartFieldMapping: report.visualization_config.fieldMapping,
              showLegend: report.visualization_config.showLegend,
              showDataLabels: report.visualization_config.showDataLabels,
            });
          }

          if (get().currentReportId !== id) return;
          await get().executeQuery();
        } catch (e) {
          console.error('Failed to load report from API:', e);
          if (get().currentReportId === id) {
            set({ queryLoading: false, queryError: 'Failed to load report' });
          }
        }
      },

      // --- Computed helpers ---

      getJoinableModelKeys: (): Set<string> => {
        const { selectedModelKeys, models } = get();
        if (selectedModelKeys.length === 0) return new Set();
        const primaryMeta = models.find(m => m.key === selectedModelKeys[0]);
        return new Set(primaryMeta?.joinable_models ?? []);
      },

      getDateColumns: (): AnnotatedColumn[] => {
        const { selectedModel, joinedModels, selectedModelKeys } = get();
        if (!selectedModel) return [];
        const cols = annotateColumns(selectedModel, true).filter(c => c.data_type === 'date' || c.data_type === 'timestamp');
        for (const key of selectedModelKeys.slice(1)) {
          const jm = joinedModels[key];
          if (jm) cols.push(...annotateColumns(jm, false).filter(c => c.data_type === 'date' || c.data_type === 'timestamp'));
        }
        return cols;
      },

      getAllColumns: (): Record<string, DataModelColumn> => {
        const { selectedModel, joinedModels } = get();
        if (!selectedModel) return {};
        const all: Record<string, DataModelColumn> = { ...selectedModel.columns };
        for (const [modelKey, jm] of Object.entries(joinedModels)) {
          for (const [key, col] of Object.entries(jm.columns)) {
            all[`${modelKey}.${key}`] = col;
          }
        }
        return all;
      },

      getDimensionColumns: (): AnnotatedColumn[] => {
        const { selectedModel, joinedModels, selectedModelKeys } = get();
        if (!selectedModel) return [];
        const cols = annotateColumns(selectedModel, true).filter(c => c.is_dimension);
        for (const key of selectedModelKeys.slice(1)) {
          const jm = joinedModels[key];
          if (jm) cols.push(...annotateColumns(jm, false).filter(c => c.is_dimension));
        }
        return cols;
      },

      getMeasureColumns: (): AnnotatedColumn[] => {
        const { selectedModel, joinedModels, selectedModelKeys } = get();
        if (!selectedModel) return [];
        const cols = annotateColumns(selectedModel, true).filter(c => c.is_measure);
        for (const key of selectedModelKeys.slice(1)) {
          const jm = joinedModels[key];
          if (jm) cols.push(...annotateColumns(jm, false).filter(c => c.is_measure));
        }
        return cols;
      },

      getFilterableColumns: (): AnnotatedColumn[] => {
        const { selectedModel, joinedModels, selectedModelKeys } = get();
        if (!selectedModel) return [];
        const cols = annotateColumns(selectedModel, true).filter(c => c.filterable);
        for (const key of selectedModelKeys.slice(1)) {
          const jm = joinedModels[key];
          if (jm) cols.push(...annotateColumns(jm, false).filter(c => c.filterable));
        }
        return cols;
      },

      getSortableColumns: (): Array<{ key: string; label: string; sourceModelName: string }> => {
        const { selectedDimensions, selectedMeasures, timeGroupings } = get();
        const dimensionCols = get().getDimensionColumns();
        const cols: Array<{ key: string; label: string; sourceModelName: string }> = [];
        for (const refKey of selectedDimensions) {
          const col = dimensionCols.find(c => c.refKey === refKey);
          if (col?.sortable) cols.push({ key: col.refKey, label: col.label, sourceModelName: col.sourceModelName });
        }
        for (const tg of timeGroupings) {
          const outputKey = `${tg.column}_${tg.granularity}`;
          const col = dimensionCols.find(c => c.refKey === tg.column);
          const label = col ? `${col.label} (${tg.granularity.charAt(0).toUpperCase() + tg.granularity.slice(1)})` : outputKey;
          cols.push({ key: outputKey, label, sourceModelName: col?.sourceModelName ?? '' });
        }
        for (const m of selectedMeasures) {
          cols.push({ key: m.alias, label: m.alias, sourceModelName: '' });
        }
        return cols;
      },
    }),
    { name: 'report-builder-store' }
  )
);
